import React from 'react';
import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  ProgressBar,
  Badge,
  Box,
} from '@shopify/polaris';

interface UsageProgressBarProps {
  used: number;
  limit: number | null;
  periodStart?: Date;
  periodEnd?: Date;
  showDetails?: boolean;
}

export const UsageProgressBar: React.FC<UsageProgressBarProps> = ({
  used,
  limit,
  periodStart,
  periodEnd,
  showDetails = true,
}) => {
  // Unlimited plan
  if (limit === null) {
    return (
      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between">
            <Text variant="headingSm" as="h3">Order Sync Usage</Text>
            <Badge tone="success">Unlimited</Badge>
          </InlineStack>
          <Text as="span" variant="bodyMd">
            {used.toLocaleString()} orders synced this period
          </Text>
          <ProgressBar progress={0} size="small" tone="primary" />
        </BlockStack>
      </Card>
    );
  }

  const percentUsed = Math.min(Math.round((used / limit) * 100), 100);
  const remaining = Math.max(0, limit - used);

  // Determine tone based on usage. Polaris ProgressBar's `tone` doesn't accept
  // 'warning', so we map the at-risk state to 'highlight' for the bar while
  // keeping 'warning' on the Badge (which does accept it).
  let tone: 'success' | 'highlight' | 'critical' = 'success';
  let badgeTone: 'success' | 'warning' | 'critical' = 'success';

  if (percentUsed >= 100) {
    tone = 'critical';
    badgeTone = 'critical';
  } else if (percentUsed >= 80) {
    tone = 'highlight';
    badgeTone = 'warning';
  }

  const formatDate = (date?: Date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between">
          <Text variant="headingSm" as="h3">Order Sync Usage</Text>
          <Badge tone={badgeTone}>
            {percentUsed >= 100 ? 'Limit Reached' : `${percentUsed}% Used`}
          </Badge>
        </InlineStack>

        <ProgressBar progress={percentUsed} size="small" tone={tone} />

        <InlineStack align="space-between">
          <Text as="span" variant="bodySm" tone="subdued">
            {used.toLocaleString()} of {limit.toLocaleString()} orders
          </Text>
          <Text as="span" variant="bodySm" tone="subdued">
            {remaining.toLocaleString()} remaining
          </Text>
        </InlineStack>

        {showDetails && periodStart && periodEnd && (
          <Box paddingBlockStart="200">
            <Text as="span" variant="bodySm" tone="subdued">
              Billing period: {formatDate(periodStart)} - {formatDate(periodEnd)}
            </Text>
          </Box>
        )}

        {percentUsed >= 80 && percentUsed < 100 && (
          <Box
            paddingBlockStart="200"
            paddingBlockEnd="100"
            paddingInlineStart="300"
            paddingInlineEnd="300"
            background="bg-surface-warning"
            borderRadius="200"
          >
            <Text as="span" variant="bodySm" tone="caution">
              You're approaching your order limit. Consider upgrading to avoid sync interruptions.
            </Text>
          </Box>
        )}

        {percentUsed >= 100 && (
          <Box
            paddingBlockStart="200"
            paddingBlockEnd="100"
            paddingInlineStart="300"
            paddingInlineEnd="300"
            background="bg-surface-critical"
            borderRadius="200"
          >
            <Text as="span" variant="bodySm" tone="critical">
              Order limit reached. Upgrade your plan to continue syncing orders.
            </Text>
          </Box>
        )}
      </BlockStack>
    </Card>
  );
};

export default UsageProgressBar;
